import { makeStyles } from "@fluentui/react-components";
import { useNavigate } from "react-router-dom";
import { Metric, MetricPanel } from "@/components/metric-panel";
import type { EvmSummary } from "@/engine/health";
import type { Project } from "@/engine/types";
import { useMoneyMasked } from "@/features/access/use-plan-access";
import { COST_KPI, costEvmOf, costKpiGap, costKpiText, type CostKpiGap } from "./cost-evm";

const useStyles = makeStyles({
  /** 7칸을 4열에 맞춘다. 둘째 줄의 첫 칸은 왼쪽 선을 갖지 않는다. */
  grid: {
    rowGap: "12px",
    "&& > *:nth-child(4n + 1)": {
      borderLeft: "none",
      paddingLeft: "0px",
    },
  },
});

function costHref(projectId: string, gap: CostKpiGap | null): string | null {
  if (gap === "ratio") return null;
  if (gap === "budget") return `/projects/${projectId}/budget`;
  return `/projects/${projectId}/budget?view=actual`;
}

/** 현황·예측이 같이 쓰는 비용 성과. 숫자는 실제 원가로, 예산이 없으면 예산으로 잇는다. */
export function CostEvmStrip({ project, evm, earnedPercent }: { project: Project; evm: Pick<EvmSummary, "ev" | "pv" | "hasBaseline">; earnedPercent?: number }) {
  const styles = useStyles();
  const navigate = useNavigate();
  const { masked } = useMoneyMasked();
  const cost = costEvmOf(project, evm, earnedPercent);
  return (
    <MetricPanel columns={4} className={styles.grid} ariaLabel="비용 성과">
      {COST_KPI.map((item) => {
        const gap = costKpiGap(cost, item.key);
        const text = costKpiText(cost, item.key, masked);
        const href = costHref(project.id, gap);
        const tone = gap ? ("muted" as const) : item.key === "cpi" && cost.cpi != null && cost.cpi < 0.9 ? ("danger" as const) : item.key === "cpi" && cost.cpi != null && cost.cpi < 1 ? ("warning" as const) : undefined;
        const where = gap === "budget" ? "예산" : href ? "실제 원가" : "";
        return (
          <Metric
            key={item.key}
            compact
            dotted={href != null}
            label={item.label}
            value={text}
            tone={tone}
            onClick={href ? () => navigate(href) : undefined}
            ariaLabel={where ? `${item.label} ${text}. ${where}` : `${item.label} ${text}`}
          />
        );
      })}
    </MetricPanel>
  );
}
